import { 
    NEW_BOOKING, ADD_SECTOR, DELETE_SECTOR, ADD_PRODUCT, UPDATE_SECTOR, 
    UPDATE_SECTOR_ACCOMMODATION, UPDATE_BOOKING_STATUS, UPDATE_PRODUCT_SECTOR,
    PAY_BOOKING, NEW_BOOKING_QUOTE
} from '../graphql/mutations/booking';
import { GET_BOOKING } from '../graphql/queries/getBooking';
import { GET_NEXT_DESTINATION } from '../graphql/queries/getCities';
import { SET_BOOKING_DETAILS, SET_NEXT_DESTINATIONS, UPDATE_SECTOR_LOADING, BOOKING_DETAILS_LOADING, SET_EXPIRED_MODAL } from './types';
import { get } from '../helpers/objectHelper';
import { store } from '../index';
import { history } from '../components/App';


/**
 * Fetch booking
 * 
 *
 * @param {object} client - Apollo client
 * @param {string} uuid - id of the booking
 * 
 */
export async function FetchBooking(client, uuid){
    try{
        await store.dispatch({type:BOOKING_DETAILS_LOADING, payload:true});
        const response = await client.query({query:GET_BOOKING, variables:{uuid:uuid}, fetchPolicy:"no-cache"});    
        
        if(response.data.getBooking.expired || response.data.getBooking.errorCode === "BOOKING_EXPIRED") await store.dispatch({type:SET_EXPIRED_MODAL, payload:true});

        await store.dispatch({ type:SET_BOOKING_DETAILS, payload:response.data.getBooking});
        await store.dispatch({type:BOOKING_DETAILS_LOADING, payload:false});
        return response;
    }
   catch({graphQLErrors}){
        if(graphQLErrors.length > 0){
            const hasResponseUrl = get([0, 'extensions', 'response', 'url'], graphQLErrors, "");
            const hasReponseStatus = get([0, 'extensions', 'response', 'status'], graphQLErrors, "");
            const hasResponseCode = get([0, 'extensions', 'code'], graphQLErrors, "");
            console.error(`${graphQLErrors[0].message} : ${hasResponseUrl}`);
            if(hasReponseStatus === 500 || hasResponseCode === "INTERNAL_SERVER_ERROR"){ 
                window.location.href = `${process.env.REACT_APP_PLANNER_URL}${process.env.REACT_APP_ERROR_PAGE}`;
            }
            if(hasResponseCode === "NOT_FOUND") store.dispatch({type:SET_EXPIRED_MODAL, payload:true});
            
            return graphQLErrors[0].message;
        }
    }
}


/**
 * New booking - Creates a new booking, adds a first sector and fetches next destination 
 * 
 *
 * @param {object} client - Apollo client
 * @param {string} currency - ISO Currency code
 * @param {string} type - Booking type: planner / regular  
 * 
 */
export async function NewBooking(client, currency, type){

    try{
        //1. Update loading state
        await store.dispatch({type:UPDATE_SECTOR_LOADING, payload:true});

        //2. New booking mutation
        const response = await client.mutate({mutation:NEW_BOOKING, variables:{currency:currency, type:type}});

        await store.dispatch({type:SET_BOOKING_DETAILS, payload:response.data.newBooking});

        await store.dispatch({type:UPDATE_SECTOR_LOADING, payload:false});

        return response;
        
    }
    catch({graphQLErrors}){
        await store.dispatch({type:UPDATE_SECTOR_LOADING, payload:false});
        if(graphQLErrors.length > 0){
            const hasResponseUrl = get([0, 'extensions', 'response', 'url'], graphQLErrors, "");
            const hasReponseStatus = get([0, 'extensions', 'response', 'status'], graphQLErrors, "");
            console.error(`${graphQLErrors[0].message} : ${hasResponseUrl}`);
            if(hasReponseStatus === 500) history.push("/500");
            return graphQLErrors[0].message;
        }
    }    
}


/**
 * Add sector and fetches next destination 
 * 
 *
 * @param {object} client - Apollo client
 * @param {string} bookingId - id of the booking created in the "NewBooking"
 * @param {string} tourId - CYO/CYU for for products the Product/tour id IT1 etc,
 * @param {string} type - Sector type: start, city, product
 * @param {string} to - destination city / for start sector the start city
 * @param {int} nights - number of nights for a stop, for start sector defaults to 0, but it can be set to any value, for city stops number of nights is mandatory
 * @param {string} date - for start sector arrival date in the start city
 * @param {string} name - name of the sector
 *
 */
 export async function AddSector(client, bookingId, tourId, type, to, nights, date, name){
    try{
        //1. Update loading state
        await store.dispatch({type:UPDATE_SECTOR_LOADING, payload:true});

        //2. Add a new sector / fetch next destinations / update loading state
        const response = await client.mutate({mutation:ADD_SECTOR, variables:{bookingId:bookingId, tourId:tourId, type:type, to:to, date:date, nights:nights, name:name}});
        await store.dispatch({type:SET_BOOKING_DETAILS, payload:response.data.addSector});

        const getDestination = await client.query({query:GET_NEXT_DESTINATION, variables:{bookingId:bookingId, maxWait:30}, fetchPolicy:"no-cache"});
        await store.dispatch({ type: SET_NEXT_DESTINATIONS, payload:getDestination.data.next});

        await store.dispatch({type:UPDATE_SECTOR_LOADING, payload:false});

        return getDestination;
    }
    catch({graphQLErrors}){
        await store.dispatch({type:UPDATE_SECTOR_LOADING, payload:false});
        if(graphQLErrors.length > 0){
            const hasResponseUrl = get([0, 'extensions', 'response', 'url'], graphQLErrors, "");
            const hasReponseStatus = get([0, 'extensions', 'response', 'status'], graphQLErrors, "");
            console.error(`${graphQLErrors[0].message} : ${hasResponseUrl}`);
            if(hasReponseStatus === 500) history.push("/500");
            return graphQLErrors[0].message;
        }
    } 
 }


 /**
 * Delete sector and fetches next destination 
 *
 * @param {object} client - Apollo client
 * @param {string} bookingId - id of the current booking
 * @param {string} sectorId - id of the city sector
 * @param {string} isAdventurePlanner - is product or select
 *
 */
export async function DeleteSector(client, bookingId, sectorId, isAdventurePlanner = null){
    try{
        //1. Update loading state
        await store.dispatch({type:UPDATE_SECTOR_LOADING, payload:true});

        //2. Add a new sector / fetch next destinations / update loading state
        const response = await client.mutate({mutation:DELETE_SECTOR, variables:{bookingId:bookingId, sectorId:sectorId}});
        await store.dispatch({type:SET_BOOKING_DETAILS, payload:response.data.deleteSector});

        await store.dispatch({type:UPDATE_SECTOR_LOADING, payload:false});

        if(isAdventurePlanner === null){
            await store.dispatch({type:UPDATE_SECTOR_LOADING, payload:true});
            
            const getDestination = await client.query({query:GET_NEXT_DESTINATION, variables:{bookingId:bookingId, maxWait:30}, fetchPolicy:"no-cache"});
            await store.dispatch({ type: SET_NEXT_DESTINATIONS, payload:getDestination.data.next});

            await store.dispatch({type:UPDATE_SECTOR_LOADING, payload:false});

            return getDestination;
        }else{
            return response
        }    

    }
    catch({graphQLErrors}){
        await store.dispatch({type:UPDATE_SECTOR_LOADING, payload:false});
        if(graphQLErrors.length > 0){
            const hasResponseUrl = get([0, 'extensions', 'response', 'url'], graphQLErrors, "");
            const hasReponseStatus = get([0, 'extensions', 'response', 'status'], graphQLErrors, "");
            console.error(`${graphQLErrors[0].message} : ${hasResponseUrl}`);
            if(hasReponseStatus === 500) history.push("/500");
            return graphQLErrors[0].message;
        }
    } 
}



 /**
 * Add product and fetches next destination 
 *
 * @param {object} client - Apollo client
 * @param {string} bookingId - id of the booking created in the "NewBooking"
 * @param {string} tourId - CYO/CYU for for products the Product/tour id IT1 etc,
 * @param {string} type - Sector type: start, city, product
 *
 */
export async function AddProduct(client, bookingId, tourId, type, date, name, isBuilder = false){
    try{
        //1. Update loading state
        await store.dispatch({type:UPDATE_SECTOR_LOADING, payload:true});

        //2. Add a new sector / fetch next destination update loading state
        const response = await client.mutate({mutation:ADD_PRODUCT, variables:{bookingId:bookingId, tourId:tourId, type:type, date:date, name:name}});
        await store.dispatch({type:SET_BOOKING_DETAILS, payload:response.data.addProduct});

        if(isBuilder){
            const getDestination = await client.query({query:GET_NEXT_DESTINATION, variables:{bookingId:bookingId, maxWait:30}, fetchPolicy:"no-cache"});
            await store.dispatch({ type: SET_NEXT_DESTINATIONS, payload:getDestination.data.next});
        }

        await store.dispatch({type:UPDATE_SECTOR_LOADING, payload:false});

        return response;
    }
    catch({graphQLErrors}){
        await store.dispatch({type:UPDATE_SECTOR_LOADING, payload:false});
        if(graphQLErrors.length > 0){
            const hasResponseUrl = get([0, 'extensions', 'response', 'url'], graphQLErrors, "");
            const hasReponseStatus = get([0, 'extensions', 'response', 'status'], graphQLErrors, "");
            console.error(`${graphQLErrors[0].message} : ${hasResponseUrl}`);
            if(hasReponseStatus === 500) history.push("/500");
            return graphQLErrors[0].message;
        }
    } 
}


 /**
 * Update and extends sector
 *
 * @param {object} client - Apollo client
 * @param {int} bookingId - Id of the booking created in the "NewBooking"
 * @param {int} sectorId - Id of sector to be updated
 * @param {int} nights - The number of nights for the sector
 * @param {string} date - date to be updated
 * @param {boolean} update - if set to true, the number of nights for the sector will be updated to the nights param passed above, if it is not present or if it is set to false it will extend the previous value by the nights param value.
 * @param {boolean} isPlanner - is update sector used on planner page
 *
 */
export async function UpdateSector(client, bookingId, sectorId, nights, date, update, isPlanner = false){
    try{
        //1. Update loading state
        await store.dispatch({type:UPDATE_SECTOR_LOADING, payload:true});

        //2. Update or extends sector
        const response = await client.mutate({mutation:UPDATE_SECTOR, variables:{bookingId:bookingId, sectorId:sectorId, nights:nights, date:date, update:update}});
        await store.dispatch({type:SET_BOOKING_DETAILS, payload:response.data.updateSector});
        await store.dispatch({type:UPDATE_SECTOR_LOADING, payload:false});

        //3. Get next destinations for the times
        if(isPlanner){
            await store.dispatch({type:UPDATE_SECTOR_LOADING, payload:true});

            const getDestination = await client.query({query:GET_NEXT_DESTINATION, variables:{bookingId:bookingId, maxWait:30}, fetchPolicy:"no-cache"});
            await store.dispatch({ type: SET_NEXT_DESTINATIONS, payload:getDestination.data.next});
            await store.dispatch({type:UPDATE_SECTOR_LOADING, payload:false});
        }

        return response;

    }
    catch({graphQLErrors}){
        await store.dispatch({type:UPDATE_SECTOR_LOADING, payload:false});
        if(graphQLErrors.length > 0){
            const hasResponseUrl = get([0, 'extensions', 'response', 'url'], graphQLErrors, "");
            const hasReponseStatus = get([0, 'extensions', 'response', 'status'], graphQLErrors, "");
            console.error(`${graphQLErrors[0].message} : ${hasResponseUrl}`);
            if(hasReponseStatus === 500) history.push("/500");
            return graphQLErrors[0].message;
        }
    } 
}



 /**
 * Update sector accommodation
 *
 * @param {object} client - Apollo client
 * @param {int} sectorId - Sector id you want to modify the accommodation
 * @param {string} propertyType - property type string - get it from the options
 * @param {string} reservationType - reservation Type string - get it from the options
 * @param {string} roomType - room type - get it from the options or none if you wish to remove accommodation
 * @param {string} roomCode - room code (ID) - get it from the options or Hotel Room object
 * @param {string} currency - Booking currency string 
 *
 */
export async function UpdateSectorAccommodation(client, sectorId, propertyType, reservationType, roomType, roomCode, currency){
    try{
        //1. Update loading state
        await store.dispatch({type:UPDATE_SECTOR_LOADING, payload:true});

        //2. Update sector accommodation
        const response = await client.mutate({mutation: UPDATE_SECTOR_ACCOMMODATION, variables:{sectorId:sectorId, propertyType:propertyType, reservationType:reservationType, roomType:roomType, roomCode:roomCode, currency:currency}});
        await store.dispatch({type:SET_BOOKING_DETAILS, payload:response.data.updateAccommodation});
        await store.dispatch({type:UPDATE_SECTOR_LOADING, payload:false});
        return response;

    }
    catch({graphQLErrors}){
        await store.dispatch({type:UPDATE_SECTOR_LOADING, payload:false});
        if(graphQLErrors.length > 0){
            const hasResponseUrl = get([0, 'extensions', 'response', 'url'], graphQLErrors, "");
            const hasReponseStatus = get([0, 'extensions', 'response', 'status'], graphQLErrors, "");
            console.error(`${graphQLErrors[0].message} : ${hasResponseUrl}`);
            if(hasReponseStatus === 500) history.push("/500");
            return graphQLErrors[0].message;
        }
    } 
}


 /**
 * Update the status of the booking
 *
 * @param {object} client - Apollo client
 * @param {string} bookingId - Id of the booking created
 * @param {string} status - Status of the bookinh
 */
export async function UpdateBookingStatus(client, bookingId, status){
    try{
        //update booking status
        const response = await client.mutate({mutation: UPDATE_BOOKING_STATUS, variables:{bookingId:bookingId, status:status}});
        return response;
    }
    catch({graphQLErrors}){
        if(graphQLErrors.length > 0){
            const hasResponseUrl = get([0, 'extensions', 'response', 'url'], graphQLErrors, "");
            const hasReponseStatus = get([0, 'extensions', 'response', 'status'], graphQLErrors, "");
            console.error(`${graphQLErrors[0].message} : ${hasResponseUrl}`);
            if(hasReponseStatus === 500) history.push("/500");
            return graphQLErrors[0].message;
        }    
    }
}


 /**
 * Update product sector
 *
 * @param {object} client - Apollo client
 * @param {string} bookingId - Id of the booking created
 * @param {int} sectorId - Sector id you want to modify the product
 * @param {string} date - new product departure date
 */
export async function UpdateProductSector(client, bookingId, sectorId, date){
    try{
        //1. Update loading state
        await store.dispatch({type:UPDATE_SECTOR_LOADING, payload:true});

        //2. Update product sector
        const response = await client.mutate({mutation: UPDATE_PRODUCT_SECTOR, variables:{bookingId:bookingId, sectorId:sectorId, date:date}});
        await store.dispatch({type:SET_BOOKING_DETAILS, payload:response.data.updateProductSector});
        await store.dispatch({type:UPDATE_SECTOR_LOADING, payload:false});
        return response;

    }
    catch({graphQLErrors}){
        await store.dispatch({type:UPDATE_SECTOR_LOADING, payload:false});
        if(graphQLErrors.length > 0){
            const hasResponseUrl = get([0, 'extensions', 'response', 'url'], graphQLErrors, "");
            const hasReponseStatus = get([0, 'extensions', 'response', 'status'], graphQLErrors, "");
            console.error(`${graphQLErrors[0].message} : ${hasResponseUrl}`);
            if(hasReponseStatus === 500) history.push("/500");
            return graphQLErrors[0].message;
        }    
    }
}


 /**
 * Pay booking
 *
 * @param {object} client - Apollo client
 * @param {string} tmsBookingId - TMS Id of the booking
 * @param {string} paymentType - Payment type of the booking deposit / full
 * @param {string} currency - ISO currency code 
 * @param {string} paymentMethod - The agent payment type
 * @param {string} reference - what the agent inputs into your reference field on payment page
 * @param {string} paymentReference - what the agent inputs into payment reference on payment page
 */
export async function PayBooking(client, tmsBookingId, paymentType, currency, paymentMethod, reference, paymentReference){
    try{
        //1. Update loading state
        await store.dispatch({type:UPDATE_SECTOR_LOADING, payload:true});

        //2. Pay booking
        const response = await client.mutate({mutation: PAY_BOOKING, variables:{tmsBookingId:tmsBookingId, paymentType:paymentType, currency:currency, paymentMethod:paymentMethod, reference:reference, paymentReference:paymentReference}});
        return response;

    }
    catch({graphQLErrors}){
        await store.dispatch({type:UPDATE_SECTOR_LOADING, payload:false});
        if(graphQLErrors.length > 0){
            const hasResponseUrl = get([0, 'extensions', 'response', 'url'], graphQLErrors, "");
            const hasReponseStatus = get([0, 'extensions', 'response', 'status'], graphQLErrors, "");
            console.error(`${graphQLErrors[0].message} : ${hasResponseUrl}`);
            if(hasReponseStatus === 500) history.push("/500");
            return graphQLErrors[0].message;
        }    
    }    
}



 /**
 * Pay booking
 *
 * @param {object} client - Apollo client
 * @param {string} uuid - id of the booking 
 * 
 */
export async function NewBookingQuote(client, uuid){
    try{
        await store.dispatch({type:UPDATE_SECTOR_LOADING, payload:true});

        const response = await client.mutate({mutation:NEW_BOOKING_QUOTE, variables:{uuid:uuid}});

        //TESING    
        // await store.dispatch({type:SET_BOOKING_DETAILS, payload:response.data.getNewBookingQuote});

        await store.dispatch({type:UPDATE_SECTOR_LOADING, payload:false});

        return response;

    }
    catch({graphQLErrors}){
        await store.dispatch({type:UPDATE_SECTOR_LOADING, payload:false});
        if(graphQLErrors.length > 0){
            const hasResponseUrl = get([0, 'extensions', 'response', 'url'], graphQLErrors, "");
            const hasReponseStatus = get([0, 'extensions', 'response', 'status'], graphQLErrors, "");
            console.error(`${graphQLErrors[0].message} : ${hasResponseUrl}`);
            if(hasReponseStatus === 500) history.push("/500");
            return graphQLErrors[0].message;
        }
    }   
}